import React, { useState, useMemo } from 'react';
import { CheckCircle2, AlertTriangle, Info, RotateCcw, Zap } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════
// PILOTE QUALITÉ · M12 · Point de patinage / Embrayage
// Œuvre pédagogique interactive :
//  · Vue écorchée du mécanisme : Moteur → Disque volant → Disque embrayage → Boîte
//  · Slider vertical de pédale (0% relâchée à 100% enfoncée)
//  · Les 2 disques se rapprochent/écartent selon la position
//  · Jauge de couplage (0-100%) + jauge régime moteur
//  · Panneau : état actuel (débrayé/patinage/embrayé) + diagnostic
// ══════════════════════════════════════════════════════════════════════════

// Description des 4 zones selon la position de la pédale
function getEtat(pos) {
  // pos : 0 = relâchée (embrayé), 100 = enfoncée à fond (débrayé)
  if (pos > 70) {
    return {
      key: 'debraye',
      titre: "Débrayé",
      subtitre: "Pédale enfoncée à fond",
      color: '#3b82f6',
      icone: 'D',
      coupling: 0,
      rpm: 850,
      voiture: "immobile",
      moteurBruit: "au ralenti",
      diagnostic: "Moteur et boîte sont TOTALEMENT SÉPARÉS. Tu peux passer une vitesse sans dommage. La voiture est libre de bouger (roue libre) ou immobile selon le frein.",
      bonGeste: "C'est la position exacte pour PASSER une vitesse. Débraye COMPLÈTEMENT avant de manipuler le levier.",
      dangerRating: 'safe',
    };
  }
  if (pos > 40) {
    return {
      key: 'approche',
      titre: "Remontée lente",
      subtitre: "Les disques se rapprochent",
      color: '#f59e0b',
      icone: '↑',
      coupling: 20,
      rpm: 900,
      voiture: "immobile",
      moteurBruit: "presque au ralenti",
      diagnostic: "Les 2 disques d'embrayage ne se touchent pas encore. Aucun couple transmis à la boîte. La voiture reste immobile.",
      bonGeste: "Continuer à remonter TRÈS DOUCEMENT. Tu approches du point critique.",
      dangerRating: 'attention',
    };
  }
  if (pos > 20) {
    return {
      key: 'patinage',
      titre: "🎯 POINT DE PATINAGE",
      subtitre: "Les disques se touchent partiellement",
      color: '#dc2626',
      icone: '~',
      coupling: 55,
      rpm: 1200,
      voiture: "bouge lentement",
      moteurBruit: "sous charge",
      diagnostic: "MOMENT PRÉCIS où les 2 disques COMMENCENT à s'accrocher. Un peu de couple passe = la voiture avance TOUTE SEULE, même sans accélérer. À sentir dans les jambes.",
      bonGeste: "Maintenir cette position 1-2 secondes pour bien SENTIR. Puis ajouter accélérateur en continuant à relâcher.",
      dangerRating: 'critical',
    };
  }
  return {
    key: 'embraye',
    titre: "Embrayé",
    subtitre: "Pédale complètement relâchée",
    color: '#22c55e',
    icone: 'E',
    coupling: 100,
    rpm: 1800,
    voiture: "roule normalement",
    moteurBruit: "en charge",
    diagnostic: "Les 2 disques d'embrayage sont TOTALEMENT collés. 100% du couple moteur passe aux roues. La voiture roule normalement.",
    bonGeste: "Pose le pied gauche AU SOL À GAUCHE de la pédale — jamais posé DESSUS sinon usure prématurée.",
    dangerRating: 'safe',
  };
}

// Jauge horizontale colorée
function JaugeH({ value, max, label, color, unit, x, y, width = 140 }) {
  const pct = Math.min(1, value / max);
  return (
    <g transform={`translate(${x} ${y})`}>
      <text x={0} y={-6} fill="#94a3b8" fontSize={11} fontWeight="bold" fontFamily="system-ui">{label}</text>
      <rect x={0} y={0} width={width} height={14} rx={7} fill="#0b1120" stroke="#334155" strokeWidth={1} />
      <rect x={2} y={2} width={(width - 4) * pct} height={10} rx={5} fill={color} />
      <text x={width + 8} y={11} fill="#fff" fontSize={11} fontWeight="bold" fontFamily="monospace">
        {Math.round(value)}{unit}
      </text>
    </g>
  );
}

export default function PointPatinageSvg() {
  const [pos, setPos] = useState(100);
  const etat = useMemo(() => getEtat(pos), [pos]);

  // Distance entre les 2 disques : dépend de la position pédale
  // pos 0 (embrayé) → écart 0 (collés)
  // pos 100 (débrayé) → écart 60
  const ecart = (pos / 100) * 60;

  return (
    <div className="w-full">
      {/* Barre d'outils */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setPos(100)}
          data-testid="m12-reset"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/20 text-white/70 hover:bg-white/5 text-[12px] font-semibold"
        >
          <RotateCcw size={13} /> Reset (débrayé)
        </button>
        {[100, 60, 30, 10, 0].map((p, i) => {
          const labels = ['100% Enfoncée', '60% Remontée', '30% Patinage', '10% Accéléré', '0% Relâchée'];
          return (
            <button
              key={p}
              onClick={() => setPos(p)}
              data-testid={`m12-preset-${p}`}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 text-white/70 hover:bg-white/5 hover:border-white/30 text-[11px] font-mono-spec"
            >
              {labels[i]}
            </button>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-4">
        {/* SVG principal : vue écorchée */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-b from-[#1a2434] to-[#0a1220]">
          <svg viewBox="0 0 800 620" className="w-full h-auto block max-h-[48vh] sm:max-h-[52vh]" role="img" aria-label="Vue écorchée de l'embrayage">
            <defs>
              <linearGradient id="m12-metal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#94a3b8" />
                <stop offset="50%" stopColor="#64748b" />
                <stop offset="100%" stopColor="#334155" />
              </linearGradient>
              <linearGradient id="m12-panel" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e293b" />
                <stop offset="100%" stopColor="#0f172a" />
              </linearGradient>
              <radialGradient id="m12-disc" cx="30%" cy="30%">
                <stop offset="0%" stopColor="#cbd5e1" />
                <stop offset="70%" stopColor="#64748b" />
                <stop offset="100%" stopColor="#1e293b" />
              </radialGradient>
              <filter id="m12-shadow">
                <feDropShadow dx="0" dy="4" stdDeviation="6" floodOpacity=".4" />
              </filter>
            </defs>

            {/* Fond */}
            <rect width={800} height={620} fill="url(#m12-panel)" />

            {/* Sol/bench */}
            <rect x={0} y={480} width={800} height={140} fill="#0b1120" opacity={0.6} />
            <line x1={0} y1={480} x2={800} y2={480} stroke="#475569" strokeWidth={1.5} />

            {/* Titre vue écorchée */}
            <text x={50} y={40} fill="#94a3b8" fontSize={12} fontWeight="bold" fontFamily="monospace">
              VUE EN COUPE · Moteur → Embrayage → Boîte
            </text>

            {/* MOTEUR (gauche) */}
            <g transform="translate(120 260)" filter="url(#m12-shadow)">
              <rect x={-60} y={-70} width={120} height={140} rx={12} fill="url(#m12-metal)" stroke="#0b1120" strokeWidth={2.5} />
              <text x={0} y={-40} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="bold">MOTEUR</text>
              <text x={0} y={-24} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">tourne toujours</text>
              {/* Pistons stylisés */}
              <rect x={-40} y={-10} width={20} height={40} rx={3} fill="#1e293b" stroke="#0b1120" strokeWidth={1} />
              <rect x={-10} y={-10} width={20} height={40} rx={3} fill="#1e293b" stroke="#0b1120" strokeWidth={1} />
              <rect x={20}  y={-10} width={20} height={40} rx={3} fill="#1e293b" stroke="#0b1120" strokeWidth={1} />
              {/* Refroidissement fins */}
              <line x1={-45} y1={40} x2={45} y2={40} stroke="#0b1120" strokeWidth={2} />
              <line x1={-45} y1={45} x2={45} y2={45} stroke="#0b1120" strokeWidth={2} />
              <line x1={-45} y1={50} x2={45} y2={50} stroke="#0b1120" strokeWidth={2} />
            </g>

            {/* Arbre de sortie moteur */}
            <rect x={180} y={252} width={40} height={16} fill="url(#m12-metal)" stroke="#0b1120" strokeWidth={1} />

            {/* DISQUE VOLANT (côté moteur, fixe) */}
            <g transform="translate(240 260)">
              <ellipse cx={0} cy={0} rx={12} ry={70} fill="url(#m12-disc)" stroke="#0b1120" strokeWidth={2} />
              {/* Trous décoratifs */}
              <circle cx={0} cy={-40} r={4} fill="#0b1120" />
              <circle cx={0} cy={0} r={4} fill="#0b1120" />
              <circle cx={0} cy={40} r={4} fill="#0b1120" />
              {/* Labels EN HAUT pour éviter chevauchement quand écart petit */}
              <text x={0} y={-82} textAnchor="middle" fill="#94a3b8" fontSize={10} fontWeight="bold">Disque volant</text>
              <text x={0} y={-95} textAnchor="middle" fill="#64748b" fontSize={8} fontFamily="monospace">(côté moteur)</text>
            </g>

            {/* Ligne d'écart pointillée */}
            <line x1={252} y1={260} x2={252 + ecart} y2={260} stroke="#dc2626" strokeWidth={2} strokeDasharray="3 3" opacity={0.5} />
            <text x={252 + ecart / 2} y={340} textAnchor="middle" fill="#dc2626" fontSize={10} fontFamily="monospace">
              écart : {Math.round(ecart)} mm
            </text>

            {/* DISQUE D'EMBRAYAGE (côté boîte, mobile — recule quand tu appuies) */}
            <g transform={`translate(${264 + ecart} 260)`}>
              <ellipse cx={0} cy={0} rx={12} ry={70} fill="url(#m12-disc)" stroke="#0b1120" strokeWidth={2} />
              {/* Garnitures (rouge = pièce d'usure) */}
              <ellipse cx={-2} cy={0} rx={4} ry={68} fill="#dc2626" opacity={0.7} />
              {/* Labels EN BAS */}
              <text x={0} y={95} textAnchor="middle" fill="#94a3b8" fontSize={10} fontWeight="bold">Disque embrayage</text>
              <text x={0} y={108} textAnchor="middle" fill="#64748b" fontSize={8} fontFamily="monospace">(côté boîte)</text>
            </g>

            {/* Étincelles si patinage */}
            {etat.key === 'patinage' && (
              <g>
                <circle cx={260 + ecart / 2} cy={220} r={4} fill="#fbbf24">
                  <animate attributeName="opacity" values="1;0;1" dur="0.5s" repeatCount="indefinite" />
                </circle>
                <circle cx={260 + ecart / 2} cy={300} r={3} fill="#f97316">
                  <animate attributeName="opacity" values="0;1;0" dur="0.4s" repeatCount="indefinite" />
                </circle>
              </g>
            )}

            {/* Arbre boîte de vitesses */}
            <rect x={280 + ecart} y={252} width={110} height={16} fill="url(#m12-metal)" stroke="#0b1120" strokeWidth={1} />

            {/* BOÎTE DE VITESSES */}
            <g transform="translate(450 260)" filter="url(#m12-shadow)">
              <rect x={-40} y={-70} width={90} height={140} rx={10} fill="url(#m12-metal)" stroke="#0b1120" strokeWidth={2.5} />
              <text x={5} y={-40} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="bold">BOÎTE</text>
              <text x={5} y={-24} textAnchor="middle" fill="#94a3b8" fontSize={9} fontFamily="monospace">rapports</text>
              {/* Engrenages stylisés */}
              <circle cx={-20} cy={20} r={16} fill="none" stroke="#0b1120" strokeWidth={2} />
              <circle cx={25} cy={20} r={13} fill="none" stroke="#0b1120" strokeWidth={2} />
              <text x={-20} y={24} textAnchor="middle" fill="#0b1120" fontSize={11} fontWeight="bold">1</text>
              <text x={25} y={24} textAnchor="middle" fill="#0b1120" fontSize={9} fontWeight="bold">2</text>
            </g>

            {/* Arbre de transmission vers roues */}
            <rect x={540} y={252} width={200} height={16} fill="url(#m12-metal)" stroke="#0b1120" strokeWidth={1} />
            {/* Roue schématique */}
            <circle cx={760} cy={260} r={32} fill="#0b1120" stroke="#475569" strokeWidth={3} />
            <circle cx={760} cy={260} r={10} fill="#475569" />
            <text x={760} y={320} textAnchor="middle" fill="#94a3b8" fontSize={10} fontWeight="bold">Roue motrice</text>

            {/* Indicateur de transmission de couple (flèche) */}
            {etat.coupling > 0 && (
              <g>
                <path
                  d={`M 300 260 L ${540 - ecart} 260`}
                  stroke="#22c55e"
                  strokeWidth={4 + etat.coupling / 20}
                  opacity={etat.coupling / 100}
                  markerEnd={etat.coupling > 30 ? 'url(#m12-arrow)' : ''}
                />
                <defs>
                  <marker id="m12-arrow" markerWidth="10" markerHeight="10" refX="7" refY="4" orient="auto">
                    <path d="M0,0 L0,8 L10,4 z" fill="#22c55e" />
                  </marker>
                </defs>
              </g>
            )}

            {/* PÉDALE (droite) */}
            <g transform="translate(680 60)">
              <text x={0} y={-4} textAnchor="middle" fill="#94a3b8" fontSize={11} fontWeight="bold">Pédale gauche</text>
              {/* Rail */}
              <rect x={-4} y={0} width={8} height={140} fill="#334155" />
              {/* Pédale (position selon pos%) */}
              <g transform={`translate(0 ${pos * 1.3})`}>
                <rect x={-25} y={-6} width={50} height={12} rx={4} fill="#dc2626" stroke="#0b1120" strokeWidth={2} />
                <rect x={-20} y={-2} width={40} height={4} fill="#7f1d1d" opacity={0.5} />
              </g>
              <text x={40} y={5} fill="#94a3b8" fontSize={10} fontFamily="monospace">Relâchée</text>
              <text x={40} y={145} fill="#94a3b8" fontSize={10} fontFamily="monospace">Enfoncée</text>
            </g>

            {/* JAUGES */}
            <g transform="translate(50 510)">
              <JaugeH x={0}   y={20} value={pos}          max={100}  label="Position pédale" color="#dc2626" unit="%"     width={140} />
              <JaugeH x={220} y={20} value={etat.coupling} max={100} label="Couplage moteur/boîte" color={etat.color} unit="%" width={140} />
              <JaugeH x={470} y={20} value={etat.rpm}     max={4000} label="Régime moteur" color="#22c55e" unit=" tr/min" width={140} />
            </g>

            {/* Bandeau bas */}
            <g transform="translate(0, 580)">
              <rect width={800} height={40} fill="#000" opacity={0.65} />
              <text x={20} y={26} fill="#fff" fontSize={14} fontWeight="bold">M12 · Point de patinage</text>
              <text x={20} y={39} fill="#94a3b8" fontSize={11}>REMC C1d — Maîtriser le point de patinage</text>
              <circle cx={770} cy={20} r={16} fill="#dc2626" />
              <text x={770} y={25} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="900">CV</text>
            </g>
          </svg>

          {/* Slider principal */}
          <div className="p-4 border-t border-white/10 bg-black/40">
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="m12-pedal-slider" className="text-white/60 text-[11px] font-mono-spec uppercase tracking-wider font-bold">
                🦶 Position pédale d&apos;embrayage
              </label>
              <span className="font-mono-spec text-white text-[16px] font-bold">
                {pos}<span className="text-white/40 text-[11px]">%</span>
              </span>
            </div>
            <input
              id="m12-pedal-slider"
              type="range"
              min={0}
              max={100}
              value={pos}
              onChange={(e) => setPos(Number(e.target.value))}
              data-testid="m12-pedal-slider"
              className="w-full accent-red-500 h-2"
              aria-label="Position pédale d'embrayage"
            />
            <div className="mt-1 flex justify-between text-[10px] text-white/30 font-mono-spec">
              <span>0% relâchée</span>
              <span>50%</span>
              <span>100% enfoncée</span>
            </div>
          </div>
        </div>

        {/* Panneau droit */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-5 self-start lg:max-h-[calc(100vh-300px)] lg:overflow-y-auto lg:custom-scroll" data-testid="m12-panel">
          {/* État actuel */}
          <div className="rounded-xl border-2 p-3.5" style={{ borderColor: etat.color, background: etat.color + '18' }}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10.5px] font-mono-spec uppercase tracking-wider font-bold" style={{ color: etat.color }}>État actuel</div>
                <div className="mt-0.5 font-serif-editorial text-white text-[20px] leading-tight">{etat.titre}</div>
                <div className="text-white/60 text-[12px]">{etat.subtitre}</div>
              </div>
              <div className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-serif-editorial text-[24px] font-bold" style={{ background: etat.color, color: '#fff' }}>
                {etat.icone}
              </div>
            </div>
          </div>

          {/* Situation véhicule */}
          <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[13px]">
            <div className="text-white/50 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold mb-1.5">🚗 Situation</div>
            <div className="grid grid-cols-2 gap-2 text-white/85 text-[12.5px]">
              <div>
                <span className="text-white/50 text-[10px] block">Voiture</span>
                <span className="font-semibold">{etat.voiture}</span>
              </div>
              <div>
                <span className="text-white/50 text-[10px] block">Moteur</span>
                <span className="font-semibold">{etat.moteurBruit}</span>
              </div>
            </div>
          </div>

          {/* Diagnostic */}
          <div className="mt-3 rounded-lg border border-cyan-500/40 bg-cyan-950/20 p-3 flex items-start gap-2">
            <Info size={14} className="text-cyan-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-cyan-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Diagnostic</div>
              <p className="mt-0.5 text-white/85 text-[12.5px] leading-relaxed">{etat.diagnostic}</p>
            </div>
          </div>

          {/* Bon geste */}
          <div className="mt-3 rounded-lg border border-emerald-500/40 bg-emerald-950/20 p-3 flex items-start gap-2">
            <CheckCircle2 size={14} className="text-emerald-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-emerald-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">✓ Le bon geste</div>
              <p className="mt-0.5 text-white/85 text-[12.5px] leading-relaxed">{etat.bonGeste}</p>
            </div>
          </div>

          {/* Alerte si patinage */}
          {etat.key === 'patinage' && (
            <div className="mt-3 rounded-lg border border-red-500/50 bg-red-950/25 p-3 flex items-start gap-2 animate-pulse">
              <Zap size={14} className="text-red-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-red-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">⚡ C&apos;est LE moment !</div>
                <p className="mt-0.5 text-white/90 text-[12.5px] leading-relaxed font-semibold">
                  Tu as trouvé le point de patinage. Sens-le dans le pied gauche. C&apos;est CE point précis qu&apos;il faut mémoriser.
                </p>
              </div>
            </div>
          )}

          {/* Avertissement usure */}
          {etat.key === 'embraye' && pos === 0 && (
            <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-950/20 p-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-300 mt-0.5 shrink-0" />
              <div>
                <div className="text-amber-300 text-[10.5px] font-mono-spec uppercase tracking-wider font-bold">Piège d&apos;usure</div>
                <p className="mt-0.5 text-white/85 text-[12px] leading-relaxed">
                  Rouler avec le pied posé sur la pédale = patinage permanent = usure prématurée. Coût de remplacement : <span className="font-semibold text-amber-200">800 à 1 500 €</span>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
